import {hel_SetServerTime} from '../logics/lib';
import {GetIHeliNetwork} from '../singleton_registry';

import type {CHeliLiveChannel} from './CHeliLiveChannel';
import type {CHeliPositionChannel} from './CHeliPositionChannel';
import type {CHeliVoiceChannel} from './CHeliVoiceChannel';
import type {Json} from '../../../HeliportJS/scripts/models/json';
import type {Return} from '../../../HeliportJS/scripts/models/return';
import type {
  LiveChannelDetail,
  PositionChannelDetail,
  RefreshChannelSessionData,
  VoiceChannelDetail
} from '../types/network';
import type Peer from 'skyway-js';

export class CPosChannelFormatter {
  channel_id: PositionChannelDetail['channel_id'] | null = null;
  spatium_code: PositionChannelDetail['spatium_code'] | null = null;
  world_code: PositionChannelDetail['world_code'] | null = null;
  channel_type: PositionChannelDetail['channel_type'] | null = null;
  create_user_code: PositionChannelDetail['create_user_code'] | null = null;
  name: PositionChannelDetail['name'] | null = null;
  description: PositionChannelDetail['description'] | null = null;
  max_player_count: PositionChannelDetail['max_player_count'] | null = null;
  max_voice_player_count: PositionChannelDetail['max_voice_player_count'] | null = null;
  allow_multi_voice_channel: PositionChannelDetail['allow_multi_voice_channel'] | null = null;
  enable_default_voice_channel: PositionChannelDetail['enable_default_voice_channel'] | null = null;
  position_rtc_type: PositionChannelDetail['position_rtc_type'] | null = null;
  voice_rtc_type: PositionChannelDetail['voice_rtc_type'] | null = null;
  is_prism: PositionChannelDetail['is_prism'] | null = null;
  users: PositionChannelDetail['users'] | null = null;
  required_tickets: PositionChannelDetail['required_tickets'] | null = null;
  is_valid: boolean | null = null;
  error_code: string | null = null;
  error_data: unknown = null;

  constructor(positionChannel: CHeliPositionChannel | null) {
    if (positionChannel) {
      this.channel_id = positionChannel.getValue('channel_id');
      this.spatium_code = positionChannel.getValue('spatium_code');
      this.world_code = positionChannel.getValue('world_code');
      this.channel_type = positionChannel.getValue('channel_type');
      this.create_user_code = positionChannel.getValue('create_user_code');
      this.name = positionChannel.getValue('name');
      this.description = positionChannel.getValue('description');
      this.max_player_count = positionChannel.getValue('max_player_count');
      this.max_voice_player_count = positionChannel.getValue('max_voice_player_count');
      this.allow_multi_voice_channel = positionChannel.getValue('allow_multi_voice_channel');
      this.enable_default_voice_channel = positionChannel.getValue('enable_default_voice_channel');
      this.position_rtc_type = positionChannel.getValue('position_rtc_type');
      this.voice_rtc_type = positionChannel.getValue('voice_rtc_type');
      this.is_prism = positionChannel.getValue('is_prism');
      this.users = positionChannel.getValue('users');
      this.required_tickets = positionChannel.getValue('required_tickets') ?? null;
      this.is_valid = positionChannel.isValid() || null;
      this.error_code = positionChannel.getErrorCode() || null;
      this.error_data = positionChannel.getErrorData();
    }
  }
}

export class CVoiceChannelFormatter {
  channel_id: VoiceChannelDetail['channel_id'] | null = null;
  position_channel_id: VoiceChannelDetail['position_channel_id'] | null = null;
  channel_type: VoiceChannelDetail['channel_type'] | null = null;
  create_user_code: VoiceChannelDetail['create_user_code'] | null = null;
  name: VoiceChannelDetail['name'] | null = null;
  description: VoiceChannelDetail['description'] | null = null;
  max_player_count: VoiceChannelDetail['max_player_count'] | null = null;
  users: VoiceChannelDetail['users'] | null = null;
  is_global: VoiceChannelDetail['is_global'] | null = null;
  is_valid: boolean | null = null;
  error_code: string | null = null;
  error_data: unknown = null;

  constructor(voiceChannel: CHeliVoiceChannel | null) {
    if (voiceChannel) {
      this.channel_id = voiceChannel?.getValue('channel_id');
      this.position_channel_id = voiceChannel?.getValue('position_channel_id');
      this.channel_type = voiceChannel?.getValue('channel_type');
      this.create_user_code = voiceChannel?.getValue('create_user_code');
      this.name = voiceChannel?.getValue('name');
      this.description = voiceChannel?.getValue('description');
      this.max_player_count = voiceChannel?.getValue('max_player_count');
      this.users = voiceChannel?.getValue('users');
      this.is_global = voiceChannel.isGlobal();
      this.is_valid = voiceChannel?.isValid() || null;
      this.error_code = voiceChannel?.getErrorCode() || null;
      this.error_data = voiceChannel?.getErrorData();
    }
  }
}

export class CLiveChannelFormatter {
  channel_id: LiveChannelDetail['channel_id'] | null = null;
  spatium_code: LiveChannelDetail['spatium_code'] | null = null;
  world_code: LiveChannelDetail['world_code'] | null = null;
  position_channel_code: LiveChannelDetail['position_channel_code'] | null = null;
  name: LiveChannelDetail['name'] | null = null;
  description: LiveChannelDetail['description'] | null = null;
  max_presenter_count: LiveChannelDetail['max_presenter_count'] | null = null; // API未実装だが必要
  required_tickets: LiveChannelDetail['required_tickets'] | null = null;
  require_login: LiveChannelDetail['require_login'] | null = null;
  users: LiveChannelDetail['users'] | null = null;
  is_valid: boolean | null = null;
  error_code: string | null = null;
  error_data: unknown = null;

  constructor(liveChannel: CHeliLiveChannel | null) {
    if (liveChannel) {
      this.channel_id = liveChannel?.getValue('channel_id');
      this.spatium_code = liveChannel?.getValue('spatium_code');
      this.world_code = liveChannel?.getValue('world_code');
      this.position_channel_code = liveChannel?.getValue('position_channel_code');
      this.name = liveChannel?.getValue('name');
      this.description = liveChannel?.getValue('description');
      this.max_presenter_count = liveChannel?.getValue('max_presenter_count');
      this.required_tickets = liveChannel?.getValue('required_tickets');
      this.require_login = liveChannel?.getValue('require_login');
      this.users = liveChannel?.getValue('users');
      this.is_valid = liveChannel?.isValid() || null;
      this.error_code = liveChannel?.getErrorCode() || null;
      this.error_data = liveChannel?.getErrorData();
    }
  }
}

/** @see https://swagger-ui.cdn.v-market.work/ism-api/schemas/Property */
type InagmeStateManagerAPIProperty = {
  key: string;
  command: string;
  value: Json;
  player?: {
    channelSessionCode: string;
  };
  /** unixtime */
  lastModifiedAt?: number;
  /** lastModifiedAtのstring版 */
  lastModifiedAtString?: string;
  /** 3桁のミリ秒（000の場合は0となる） */
  lastModifiedAtMilliseconds?: number;
  /** 3桁のミリ秒の文字列（000の場合は000となる） */
  lastModifiedAtMillisecondsString?: string;
};

type IngameStateManagerAPIStatesResponse = {
  states: {
    state: {
      id: string;
      spatiumCode: string;
      worldCode: string;
      positionChannelCode?: string;
      objectId: string;
      createdAt: string;
      updatedAt: string;
    };
    objectId: string;
    properties: InagmeStateManagerAPIProperty[];
    lastUpdatedAtUnix?: number;
    personalProperties?: InagmeStateManagerAPIProperty[] | null;
  }[];
};

/** @see https://swagger-ui.cdn.v-market.work/ism-api/operations/get-world-states */
let worldItemStates: Return<IngameStateManagerAPIStatesResponse> | null = null;
/** @see https://swagger-ui.cdn.v-market.work/ism-api/operations/put-position-channel-states */
let roomItemStates: Return<IngameStateManagerAPIStatesResponse> | null = null;

export const preparePositionChannelSession = async (channelId: string): Promise<void> => {
  const resHeartbeat = await globalThis.heliport.v3.api.channelSessionApi.refreshChannelSession();
  if (resHeartbeat.status !== 'ok') {
    throw new Error(
      `channelSessionApi.refreshChannelSession() failed: ${JSON.stringify(resHeartbeat, null, 2)}`
    );
  }
  hel_SetServerTime((resHeartbeat.data as RefreshChannelSessionData).response_timestamp);

  const [spatiumCode, worldId] = channelId.split('__');
  if (spatiumCode === undefined || worldId === undefined) {
    throw new Error();
  }

  // Worldの通知を監視する
  if (typeof resHeartbeat.data.response_timestamp === 'number') {
    await globalThis.heliport.v3.api.apiNotification.watchWorld({
      spatiumCode,
      worldId,
      baseTime: resHeartbeat.data.response_timestamp
    });
  }

  // PositionChannelの通知を監視する
  if (typeof resHeartbeat.data.response_timestamp === 'number') {
    await globalThis.heliport.v3.api.apiNotification.watchPositionChannel({
      channelId,
      baseTime: resHeartbeat.data.response_timestamp
    });
  }

  // ISMAPI繋ぎこみ
  worldItemStates = (await globalThis.heliport.v3.api.ingameStateManagerApi.fetchWorldItemStates({
    spatiumCode: spatiumCode,
    worldCode: worldId
  })) as Return<IngameStateManagerAPIStatesResponse>;
  roomItemStates = (await globalThis.heliport.v3.api.ingameStateManagerApi.fetchItemStates(
    spatiumCode,
    worldId,
    channelId
  )) as Return<IngameStateManagerAPIStatesResponse>;
};

export const joinPosChannel = async (
  channelid: string,
  authorityJwt: string | null,
  ticketJwt: string | null
): Promise<CPosChannelFormatter | null> => {
  const heliNetwork = GetIHeliNetwork();
  if (!heliNetwork) {
    return null;
  }
  const posChannel = await heliNetwork.getPosChannel(channelid);
  if (!posChannel) return null;

  await preparePositionChannelSession(channelid);

  // 入室
  if (!(await heliNetwork.joinPositionChannel(posChannel, authorityJwt, ticketJwt))) {
    // 入室失敗時
    globalThis.heliport.v3.api.apiNotification.unWatchWorld();
    globalThis.heliport.v3.api.apiNotification.unWatchPositionChannel();
    worldItemStates = null;
    roomItemStates = null;
  }

  // HeliScriptで共通のKeyで値を取得できるようにIHelinetworkの値を整形する
  const result = new CPosChannelFormatter(posChannel);

  return result;
};

export const prepareLeavePositionChannel = (): void => {
  globalThis.heliport.v3.api.apiNotification.unWatchWorld();
  globalThis.heliport.v3.api.apiNotification.unWatchPositionChannel();
};

export const joinVoiceChannel = async (voiceChannelId: string) => {
  if (voiceChannelId === null || voiceChannelId === '') return null;

  const voiceChannel = await GetIHeliNetwork()?.getVoiceChannel(voiceChannelId);
  if (voiceChannel === undefined || voiceChannel === null) return null;

  // デフォルトマイクデバイスをブラウザから取得
  const pIHeliMic = await GetIHeliNetwork()?.getMicDevice();
  if (!pIHeliMic) return null;

  // 入室
  await GetIHeliNetwork()?.joinVoiceChannel(voiceChannel, pIHeliMic);

  // HeliScriptで共通のKeyで値を取得できるようにIHelinetworkの値を整形する
  const result = new CVoiceChannelFormatter(voiceChannel);

  if (result === undefined || result === null) return null;

  return result;
};

/**
 * @deprecated
 * 戻り値の型に {@link CHeliOldSkywayUnifiedPositionVoiceChannelProvider} 特有の {@link Peer} が含まれるため、プロトコルに依存するコードの原因となっている。
 *
 * また、第1引数 `configKey` も {@link CHeliOldSkywayUnifiedPositionVoiceChannelProvider} 特有である。
 * @param configKey
 * @returns
 */
export const connect = async (configKey?: string): Promise<Peer | null | undefined> => {
  return await GetIHeliNetwork()?.connect(configKey);
};

export const getWorldItemStates = () => {
  return worldItemStates;
};

export const getRoomItemStates = () => {
  return roomItemStates;
};

export const getWorldItemStatesByObjectId = (objectId: string) => {
  const data = worldItemStates;
  if (!data) {
    throw new Error('m_WorldItemStates is null.');
  }
  if (data.status === 'ng') {
    throw new Error(
      `heliport.v3.api.ingameStateManagerApi.fetchWorldItemStates() failed: ${JSON.stringify(data, null, 2)}`
    );
  }
  const filteredData = {
    status: data.status,
    data: {
      states: data.data.states.filter((state) => state.objectId === objectId)
    }
  };
  return filteredData;
};

export const getRoomItemStatesByObjectId = (objectId: string) => {
  const data = roomItemStates;
  if (!data) {
    throw new Error('m_RoomItemStates is null.');
  }
  if (data.status === 'ng') {
    throw new Error(
      `heliport.v3.api.ingameStateManagerApi.fetchItemStates() failed: ${JSON.stringify(data, null, 2)}`
    );
  }
  const filteredData = {
    status: data.status,
    data: {
      states: data.data.states.filter((state) => state.objectId === objectId)
    }
  };
  return filteredData;
};
